import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import ErrorMessage from '../ErrorMessage';
import Dropdown from './Dropdown';
import {SelectDropdownErrorStyle, ButtonWrapperForTooltip} from './SelectDropdown';

export interface SelectDropdownWithSettingsProps {
  id?: string;
  buttonSize?: ButtonSize;
  closeOnInsideClick?: boolean;
  closeOnOutsideClick?: boolean;
  disabled?: boolean;
  displayName: React.ReactNode;
  // Whether to ellipsize the display name text. If false, text will overflow to another row.
  shouldEllipsize?: boolean;
  error?: string | null;
  errorStyle?: SelectDropdownErrorStyle;
  errorTooltipPlacement?: FloatingPlacement;
  // Icon to render to the left of `displayName`
  icon?: BenchlingIconName;
  menu: JSX.Element;
  // Applies only to the dropdown button element
  autoFocus?: boolean;
  alwaysDropDown?: boolean;
  dataTestAttrs?: DataTestAttrs;
  // Allows the display of a settings icon button to the right of the dropdown caret. Used to control submenus
  // relevant to the dropdown
  settingsIconProps: {
    isActive: boolean;
    onClick: () => void;
  };
}

/**
 * Select-style dropdown (our version of <select>...</select>) with a default selection and a settings button
 * for opening/closing a submenu with related options.
 *
 * The parent is responsible for updating the display name to match the current selection and the submenu.
 */
export default function SelectDropdownWithSettings({
  alwaysDropDown = false,
  autoFocus,
  buttonSize,
  closeOnInsideClick = true,
  closeOnOutsideClick = true,
  dataTestAttrs,
  disabled = false,
  displayName,
  error,
  errorStyle = SelectDropdownErrorStyle.INLINE,
  errorTooltipPlacement,
  icon,
  menu,
  settingsIconProps,
  shouldEllipsize = false,
}: SelectDropdownWithSettingsProps): JSX.Element {
  // If the dropdown is disabled and ellipsized, users may not be able to see what value is selected, so we
  // render a tooltip.
  const tooltip =
    errorStyle === SelectDropdownErrorStyle.TOOLTIP && error
      ? error
      : shouldEllipsize && disabled && typeof displayName === 'string'
        ? displayName
        : null;

  // the button size classes set a specific height and padding that don't allow for the button contents to
  // size according to their contents, which ends up making the bottom padding incorrect. These classes
  // override the padding from those classes so that there is enough space for the button contents
  const buttonSizeClass =
    buttonSize != null
      ? {
          [ButtonSize.MINI]: selectDropdownWithSettingsStyles.buttonMiniPaddingFix,
          [ButtonSize.SMALL]: selectDropdownWithSettingsStyles.buttonSmallPaddingFix,
          [ButtonSize.MEDIUM]: selectDropdownWithSettingsStyles.buttonMediumPaddingFix,
          [ButtonSize.LARGE]: null,
        }[buttonSize]
      : null;

  return (
    <div
      className="selectDropdown"
      {...getTestProps(dataTestAttrs ?? {component: 'SelectDropdownWithSettings', element: 'root'})}
    >
      <Tooltip text={tooltip} placement={errorTooltipPlacement} disableContentSelection={true}>
        <Dropdown
          alwaysDropDown={alwaysDropDown}
          closeOnInsideClick={closeOnInsideClick}
          closeOnOutsideClick={closeOnOutsideClick}
          menu={menu}
        >
          <ButtonWrapperForTooltip disabled={disabled}>
            <button
              className={classNames(
                'btn-af-default',
                'selectDropdown-button',
                disabled && 'is-disabled',
                buttonSize != null && `button--${buttonSize}`,
                css(selectDropdownWithSettingsStyles.noRightMargin),
                css(error != null && !disabled && selectDropdownWithSettingsStyles.errorBorder),
                css(buttonSizeClass != null && buttonSizeClass)
              )}
              type="button"
              autoFocus={autoFocus}
              {...getTestProps({component: 'SelectDropdownWithSettings', element: 'input'})}
            >
              <div className={css(selectDropdownWithSettingsStyles.buttonContent)}>
                {icon && (
                  <div className={css(selectDropdownWithSettingsStyles.iconWrapper)}>
                    <BenchlingIcon icon={icon} />
                  </div>
                )}
                <div
                  className={classNames(
                    icon && css(selectDropdownWithSettingsStyles.iconFormat),
                    css(selectDropdownWithSettingsStyles.selectDropdownTextContainer)
                  )}
                >
                  <span
                    className={classNames(
                      css(
                        shouldEllipsize && selectDropdownWithSettingsStyles.ellipsizedText,
                        selectDropdownWithSettingsStyles.selectDropdownText
                      ),
                      'selectDropdown-text'
                    )}
                  >
                    {displayName}
                  </span>
                </div>
                <div className={css(selectDropdownWithSettingsStyles.settingsButtonContainer)}>
                  <div>
                    <BenchlingIcon
                      className={css(selectDropdownWithSettingsStyles.buttonCaret)}
                      icon="chevron-down"
                    />
                  </div>
                  <div
                    className={css(selectDropdownWithSettingsStyles.settingsIconContainer)}
                    onClick={(event) => {
                      // Stop propagation so that toggling the settings doesn't also open/close the dropdown
                      event.stopPropagation();
                      if (!disabled) {
                        settingsIconProps.onClick();
                      }
                    }}
                  >
                    <BenchlingIcon
                      className={classNames(
                        disabled && 'is-disabled',
                        css(
                          selectDropdownWithSettingsStyles.settingsIcon,
                          settingsIconProps.isActive && selectDropdownWithSettingsStyles.activeSettingsIcon
                        )
                      )}
                      icon="gear"
                    />
                  </div>
                </div>
              </div>
            </button>
          </ButtonWrapperForTooltip>
        </Dropdown>
      </Tooltip>
      {error && errorStyle === SelectDropdownErrorStyle.INLINE && <ErrorMessage error={error} />}
    </div>
  );
}

/** @package */
export const selectDropdownWithSettingsStyles = StyleSheet.create({
  activeSettingsIcon: {
    color: Colors.BLUE_LIGHT,
  },
  buttonContent: {
    alignItems: 'stretch',
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonCaret: {
    color: Colors.GRAY_DARKER,
    flexShrink: 0,
    marginLeft: '5px',
  },
  iconWrapper: {
    alignItems: 'center',
    color: Colors.GRAY,
    display: 'flex',
    justifyContent: 'center',
    width: '1em',
    marginRight: '10px',
  },
  iconFormat: {
    flex: 1,
    minWidth: 0 /* Prevent overflow */,
  },
  selectDropdownTextContainer: {
    display: 'flex',
    alignItems: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  selectDropdownText: {
    overflowWrap: 'break-word',
    minWidth: 0,
    letterSpacing: 'initial',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  ellipsizedText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  errorBorder: {
    border: '1px solid',
    borderColor: Colors.RED_DARK,
  },
  noRightMargin: {
    paddingRight: '0px',
  },
  settingsButtonContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  settingsIcon: {
    color: Colors.GRAY_DARKER,
  },
  settingsIconContainer: {
    borderLeft: `1px solid ${Colors.GRAY}`,
    marginLeft: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    display: 'flex',
    alignItems: 'center',
    height: 'calc(100%)',
  },
  buttonMiniPaddingFix: {
    paddingTop: '2px',
    paddingBottom: '2px',
  },
  buttonSmallPaddingFix: {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  buttonMediumPaddingFix: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
});
